import React, { useEffect, useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import StudentContext from '../../context/StudentContext'
import generateStrongPassword from '../auth/Strongpassword'
import { copyToClipboard } from '../utils/Utilsfunc'
import AlertContext from '../../context/AlertContext'
import { useEmail } from '../../context/EmailContext'
import Sidedash from '../uicomponents/Sidedash'

const CreateStudent = () => {
	const history = useNavigate()
	const host = process.env.REACT_APP_BACKEND_URL
	const context = useContext(StudentContext)
	const { showAlert } = useContext(AlertContext)
	const { addStudent } = context;
	const [students, setstudents] = useState({ 
		name: "", 
		email: "", 
		gender: "", 
		password: "", 
		address: "", 
		phone: "", 
		parentsphone: "", 
		photo: "", 
		documentid: "", 
		role: "Student" 
	})
	const { sendEmail } = useEmail();
	const [showSpinner, setShowSpinner] = useState(false);
	const [isPhoneAvailable, setIsPhoneAvailable] = useState(true);
	const [isEmailAvailable, setIsEmailAvailable] = useState(true);
	const debounceDelay = 1000;
	const [isCheckingEmailAvailability, setIsCheckingEmailAvailability] = useState(false);
	const [isCheckingPhoneAvailability, setIsCheckingPhoneAvailability] = useState(false);

	// File upload toggle states
	const [photoUploadMode, setPhotoUploadMode] = useState('upload'); // 'upload' or 'url'
	const [documentUploadMode, setDocumentUploadMode] = useState('upload'); // 'upload' or 'url'

	// Form validation states
	const [errors, setErrors] = useState({});
	const [emailError, setEmailError] = useState("");
	const [phoneError, setPhoneError] = useState("");
	const [parentPhoneError, setParentPhoneError] = useState("");

	useEffect(() => {
		if (localStorage.getItem('token') && (localStorage.getItem('role') === 'Admin' || localStorage.getItem('role') === 'Superadmin')) {
			// Admin can access this page
		} else if (localStorage.getItem('role') === 'Student') {
			showAlert("You aren't allowed to be here", "danger")
			history("/account");
		} else {
			showAlert("Please login to continue", "warning")
			history("/login");
		}
	}, [history, showAlert]);

	const validateForm = () => {
		const newErrors = {};

		// Name validation
		if (!students.name.trim()) {
			newErrors.name = "Full name is required";
		} else if (students.name.trim().length < 2) {
			newErrors.name = "Name must be at least 2 characters";
		}

		// Email validation
		const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
		if (!students.email.trim()) {
			newErrors.email = "Email is required";
		} else if (!emailRegex.test(students.email)) {
			newErrors.email = "Please enter a valid email address";
		}

		// Gender validation
		if (!students.gender) {
			newErrors.gender = "Please select a gender";
		}

		// Password validation
		if (!students.password) {
			newErrors.password = "Password is required";
		} else if (students.password.length < 6) {
			newErrors.password = "Password must be at least 6 characters";
		}

		// Address validation
		if (!students.address.trim()) {
			newErrors.address = "Address is required";
		}

		// Phone validation
		const phoneRegex = /^\d{10}$/;
		if (!students.phone) {
			newErrors.phone = "Phone number is required";
		} else if (!phoneRegex.test(students.phone)) {
			newErrors.phone = "Please enter a valid 10-digit phone number";
		}

		// Parent phone validation
		if (!students.parentsphone) {
			newErrors.parentsphone = "Parent/Guardian phone is required";
		} else if (!phoneRegex.test(students.parentsphone)) {
			newErrors.parentsphone = "Please enter a valid 10-digit phone number";
		}

		// Photo validation
		if (photoUploadMode === 'upload') {
			if (!students.photo || (students.photo && students.photo.size === 0)) {
				newErrors.photo = "Photo is required";
			}
		} else {
			if (!students.photo || students.photo.trim() === '') {
				newErrors.photo = "Photo URL is required";
			} else {
				// Basic URL validation
				try {
					new URL(students.photo);
				} catch {
					newErrors.photo = "Please enter a valid URL";
				}
			}
		}

		// Document ID validation
		if (documentUploadMode === 'upload') {
			if (!students.documentid || (students.documentid && students.documentid.size === 0)) {
				newErrors.documentid = "Valid ID proof is required";
			}
		} else {
			if (!students.documentid || students.documentid.trim() === '') {
				newErrors.documentid = "Document URL is required";
			} else {
				// Basic URL validation
				try {
					new URL(students.documentid);
				} catch {
					newErrors.documentid = "Please enter a valid URL";
				}
			}
		}

		// Role validation
		if (!students.role) {
			newErrors.role = "Please select a role";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleClick = async (e) => {
		e.preventDefault();
		
		if (!validateForm()) {
			showAlert("Please fix the errors in the form", "danger");
			return;
		}

		if (!isEmailAvailable || !isPhoneAvailable) {
			showAlert("Email or phone number already exists", "danger");
			return;
		}

		setShowSpinner(true)
		const { name, email, gender, password, address, phone, parentsphone, photo, documentid, role } = students;

		try {
			// Prepare photo and document data based on upload mode
			let photoData = photo;
			let documentData = documentid;

			// If in URL mode, pass the URL string directly
			if (photoUploadMode === 'url') {
				photoData = photo; // URL string
			}
			if (documentUploadMode === 'url') {
				documentData = documentid; // URL string
			}

			const result = await addStudent(name, email, gender, password, address, phone, parentsphone, photoData, documentData, role);

			if (!result.success) {
				showAlert(`Failed to create student: ${result.message}`, "danger");
				setShowSpinner(false);
				return;
			}

			setShowSpinner(false)
			const attendanceCode = result.attendanceCode;
			showAlert("Student created successfully!", "success");

			// Send welcome email
			const welcomeEmailHTML = `
				<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
				<html dir="ltr" lang="en">
				<head>
					<meta content="text/html; charset=UTF-8" http-equiv="Content-Type" />
				</head>
				<body style="background-color:rgb(255,255,255);margin-top:auto;margin-bottom:auto;margin-left:auto;margin-right:auto;font-family:ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, &quot;Segoe UI&quot;, Roboto, &quot;Helvetica Neue&quot;, Arial, &quot;Noto Sans&quot;, sans-serif;">
					<div style="max-width:465px;border:1px solid #eaeaea;border-radius:4px;margin:40px auto;padding:20px;">
						<div style="text-align:center;margin:32px 0;">
							<img alt="BookBuddy" height="40" src="https://bookbuddy.co.in/wp-content/uploads/2023/02/Background.png" style="display:block;outline:none;border:none;text-decoration:none;margin:0 auto;" width="130" />
						</div>
						<h1 style="color:rgb(0,0,0);font-size:24px;font-weight:400;text-align:center;margin:30px 0 8px;">Welcome to <strong>BookBuddy</strong></h1>
						<p style="font-size:14px;line-height:24px;margin:16px 0;color:rgb(0,0,0);text-align:center;">Library &amp; Co-Study Zone</p>
						<p style="font-size:14px;line-height:24px;margin:16px 0;color:rgb(0,0,0);">Hello <strong>${students.name}</strong>,</p>
						<p style="font-size:14px;line-height:24px;margin:16px 0;color:rgb(0,0,0);">Your account has been created by our admin team. Welcome to BookBuddy!</p>
						<p style="font-size:14px;line-height:24px;margin:16px 0;color:rgb(0,0,0);"><strong>Attendance Code:</strong> ${attendanceCode}</p>
						<p style="font-size:14px;line-height:24px;margin:16px 0;color:rgb(0,0,0);"><strong>Login Email:</strong> ${students.email}</p>
						<p style="font-size:14px;line-height:24px;margin:16px 0;color:rgb(0,0,0);">For any queries, contact us at <a href="tel:7042912701">+91-7042912701</a></p>
						<p style="font-size:14px;line-height:24px;margin:16px 0;color:rgb(0,0,0);"><strong>Address:</strong> 2nd &amp; 3rd Floor, Skyline Tower, Adarsh Nagar Samastipur</p>
					</div>
				</body>
				</html>
			`;

			try {
				await sendEmail({
					to: students.email,
					subject: 'Welcome to BookBuddy - Account Created',
					html: welcomeEmailHTML,
				});
			} catch (emailError) {
				console.error('Failed to send welcome email:', emailError);
			}

			// Reset form
			setstudents({
				name: "",
				email: "",
				gender: "",
				password: "",
				address: "",
				phone: "",
				parentsphone: "",
				photo: "",
				documentid: "",
				role: "Student",
			});
			setErrors({});
			setPhotoUploadMode('upload');
			setDocumentUploadMode('upload');

		} catch (error) {
			console.error("Error creating student:", error);
			showAlert(`Error: ${error instanceof Error ? error.message : "An unknown error occurred"}`, "danger");
			setShowSpinner(false);
		}
	}

	const onChange = (e) => {
		if (e.target.type === 'file') {
			setstudents({ ...students, [e.target.name]: e.target.files[0] });
		} else if (e.target.name === 'gender') {
			setstudents({ ...students, gender: e.target.value });
		} else {
			setstudents({ ...students, [e.target.name]: e.target.value });
		}

		// Clear error when user starts typing
		if (errors[e.target.name]) {
			setErrors({ ...errors, [e.target.name]: "" });
		}

		// Handle email and phone validation
		if (e.target.name === 'phone') {
			handlePhoneChange(e.target.value);
		} else if (e.target.name === 'email') {
			handleEmailChange(e.target.value);
		} else if (e.target.name === 'parentsphone') {
			handleParentPhoneChange(e.target.value);
		}
	};

	// Debounced phone number check function
	const [checkPhoneAvailability, setCheckPhoneAvailability] = useState(null);
	useEffect(() => {
		const timeout = setTimeout(async () => {
			if (checkPhoneAvailability && checkPhoneAvailability.length === 10) {
				setIsCheckingPhoneAvailability(true);
				try {
					const response = await fetch(`${host}/students/check-phone`, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ phone: checkPhoneAvailability }),
					});
					const data = await response.json();
					setIsPhoneAvailable(data.available);
				} catch (error) {
					console.error('Error checking phone availability:', error);
				} finally {
					setIsCheckingPhoneAvailability(false);
				}
			}
		}, debounceDelay);

		return () => clearTimeout(timeout);
	}, [checkPhoneAvailability, debounceDelay, host]);

	const handlePhoneChange = (phone) => {
		const phoneRegex = /^\d{10}$/;
		if (!phoneRegex.test(phone)) {
			setPhoneError('Please enter a valid 10-digit phone number.');
			setIsCheckingPhoneAvailability(false);
			return;
		}
		setCheckPhoneAvailability(phone);
		setPhoneError('');
	};

	// Debounced email check function
	const [checkEmailAvailability, setCheckEmailAvailability] = useState(null);
	useEffect(() => {
		const timeout = setTimeout(async () => {
			if (checkEmailAvailability) {
				setIsCheckingEmailAvailability(true);
				try {
					const response = await fetch(`${host}/students/check-email`, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ email: checkEmailAvailability }),
					});
					const data = await response.json();
					setIsEmailAvailable(data.available);
				} catch (error) {
					console.error('Error checking email availability:', error);
				} finally {
					setIsCheckingEmailAvailability(false);
				}
			}
		}, debounceDelay);

		return () => clearTimeout(timeout);
	}, [checkEmailAvailability, debounceDelay, host]);

	const handleEmailChange = (email) => {
		const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
		if (!emailRegex.test(email)) {
			setEmailError('Please enter a valid email address.');
			setIsCheckingEmailAvailability(false);
			return;
		}
		setCheckEmailAvailability(email);
		setEmailError("");
	};

	const handleParentPhoneChange = (phone) => {
		const phoneRegex = /^\d{10}$/;
		if (!phoneRegex.test(phone)) {
			setParentPhoneError('Please enter a valid 10-digit phone number.');
		} else {
			setParentPhoneError('');
		}
	};

	const generatePassword = (inputId) => {
		const generatedPassword = generateStrongPassword();
		document.getElementById(inputId).value = generatedPassword;
		setstudents({ ...students, password: generatedPassword });
		copyToClipboard("passwordInput")
		showAlert('Strong password generated and copied to clipboard!', "success")
	}

	function togglePasswordVisibility(inputId) {
		const passwordInput = document.getElementById(inputId);
		const isPasswordVisible = passwordInput.type === "text";
		passwordInput.type = isPasswordVisible ? "password" : "text";
	}

	return (
		<div className="container-fluid">
			<div className="row">
				<Sidedash />
				<div className="col-md-9 pt-3">
					<div className="row justify-content-center">
						<div className="col-lg-8">
							<div className="text-center mb-4">
								<h2 className="h3">Create New Student</h2>
								<p className="text-muted">Add a new student to the system</p>
							</div>

							<div className="card">
								<div className="card-body">
									<form encType="multipart/form-data" onSubmit={handleClick} className="needs-validation" noValidate>
										<div className="row">
											<div className="col-md-6">
												<div className="mb-3">
													<label className="form-label">Full name <span className="text-danger">*</span></label>
													<input 
														className={`form-control ${errors.name ? "is-invalid" : ""}`}
														value={students.name} 
														type="text" 
														name="name" 
														placeholder="Enter full name" 
														onChange={onChange} 
														required 
													/>
													{errors.name && <div className="invalid-feedback">{errors.name}</div>}
												</div>
											</div>
											<div className="col-md-6">
												<div className="mb-3">
													<label className="form-label">Email <span className="text-danger">*</span></label>
													<input 
														className={`form-control ${errors.email || emailError || !isEmailAvailable ? "is-invalid" : ""}`}
														value={students.email} 
														type="email" 
														name="email" 
														placeholder="Enter email address" 
														onChange={onChange} 
														required 
													/>
													{isCheckingEmailAvailability && <span className='spinner-wrap'><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span></span>}
													{errors.email && <div className="invalid-feedback">{errors.email}</div>}
													{emailError && <div className="invalid-feedback">{emailError}</div>}
													{!isCheckingEmailAvailability && !emailError && !errors.email && checkEmailAvailability && <div className={`${(isEmailAvailable ? "valid-feedback" : "invalid-feedback")}`}>{isEmailAvailable ? "Available" : "Email already exists"}</div>}
												</div>
											</div>
										</div>

										<div className="row">
											<div className="col-md-6">
												<div className="mb-3">
													<label className="form-label">Password <span className="text-danger">*</span></label>
													<div className="position-relative">
														<input 
															className={`form-control ${errors.password ? "is-invalid" : ""}`}
															id="passwordInput" 
															value={students.password} 
															type="password" 
															name="password" 
															placeholder="Enter password" 
															onChange={onChange} 
															required 
														/>
														<svg onClick={() => generatePassword("passwordInput")} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" style={{ cursor: "pointer" }} className="bi bi-file-earmark-lock2-fill position-absolute top-50 end-0 translate-middle" viewBox="0 0 16 16"><path d="M7 7a1 1 0 0 1 2 0v1H7z" /><path d="M9.293 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.707A1 1 0 0 0 13.707 4L10 .293A1 1 0 0 0 9.293 0M9.5 3.5v-2l3 3h-2a1 1 0 0 1-1-1M10 7v1.076c.54.166 1 .597 1 1.224v2.4c0 .816-.781 1.3-1.5 1.3h-3c-.719 0-1.5-.484-1.5-1.3V9.3c0-.627.46-1.058 1-1.224V7a2 2 0 1 1 4 0" /></svg>
														<svg onClick={() => togglePasswordVisibility("passwordInput")} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" style={{ cursor: "pointer" }} className="bi bi-eye-fill position-absolute top-50 end-0 translate-middle mx-4" viewBox="0 0 16 16"><path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0" /><path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8m8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7" /></svg>
													</div>
													{errors.password && <div className="invalid-feedback">{errors.password}</div>}
												</div>
											</div>
											<div className="col-md-6">
												<div className="mb-3">
													<label className="form-label">Gender <span className="text-danger">*</span></label>
													<div className="mt-2">
														<div className="form-check form-check-inline">
															<input 
																className={`form-check-input ${errors.gender ? "is-invalid" : ""}`}
																value="Male" 
																type="radio" 
																name="gender" 
																id="male" 
																checked={students.gender === 'Male'} 
																onChange={onChange} 
																required 
															/>
															<label className="form-check-label" htmlFor="male">Male</label>
														</div>
														<div className="form-check form-check-inline">
															<input 
																className={`form-check-input ${errors.gender ? "is-invalid" : ""}`}
																value="Female" 
																type="radio" 
																name="gender" 
																id="female" 
																checked={students.gender === 'Female'} 
																onChange={onChange} 
																required 
															/>
															<label className="form-check-label" htmlFor="female">Female</label>
														</div>
													</div>
													{errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
												</div>
											</div>
										</div>

										<div className="mb-3">
											<label className="form-label">Address <span className="text-danger">*</span></label>
											<input 
												className={`form-control ${errors.address ? "is-invalid" : ""}`}
												value={students.address} 
												type="text" 
												name="address" 
												placeholder="Enter full address" 
												onChange={onChange} 
												required 
											/>
											{errors.address && <div className="invalid-feedback">{errors.address}</div>}
										</div>

										<div className="row">
											<div className="col-md-6">
												<div className="mb-3">
													<label className="form-label">Phone Number <span className="text-danger">*</span></label>
													<input 
														className={`form-control ${errors.phone || phoneError || !isPhoneAvailable ? 'is-invalid' : ''}`}
														value={students.phone} 
														onChange={onChange} 
														type="text" 
														name="phone" 
														placeholder="10-digit phone number" 
														required 
													/>
													{isCheckingPhoneAvailability && <span className='spinner-wrap'><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span></span>}
													{errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
													{phoneError && <div className="invalid-feedback">{phoneError}</div>}
													{!isCheckingPhoneAvailability && !phoneError && !errors.phone && checkPhoneAvailability && <div className={`${(isPhoneAvailable ? "valid-feedback" : "invalid-feedback")}`}>{isPhoneAvailable ? "Available" : "Phone number already exists"}</div>}
												</div>
											</div>
											<div className="col-md-6">
												<div className="mb-3">
													<label className="form-label">Parent/Guardian Phone <span className="text-danger">*</span></label>
													<input 
														className={`form-control ${errors.parentsphone || parentPhoneError ? "is-invalid" : ""}`}
														value={students.parentsphone} 
														type="text" 
														name="parentsphone" 
														placeholder="Guardian phone number" 
														onChange={onChange} 
														required 
													/>
													{errors.parentsphone && <div className="invalid-feedback">{errors.parentsphone}</div>}
													{parentPhoneError && <div className="invalid-feedback">{parentPhoneError}</div>}
												</div>
											</div>
										</div>

										<div className="row">
											<div className="col-md-6">
												<div className="mb-3">
													<label className="form-label">Photo <span className="text-danger">*</span></label>
													<div className="mb-2">
														<div className="form-check form-check-inline">
															<input 
																className="form-check-input" 
																type="radio" 
																id="photoUpload" 
																name="photoMode" 
																value="upload" 
																checked={photoUploadMode === 'upload'} 
																onChange={(e) => {
																	setPhotoUploadMode(e.target.value);
																	setstudents({ ...students, photo: "" });
																	setErrors({ ...errors, photo: "" });
																}} 
															/>
															<label className="form-check-label" htmlFor="photoUpload">Upload File</label>
														</div>
														<div className="form-check form-check-inline">
															<input 
																className="form-check-input" 
																type="radio" 
																id="photoUrl" 
																name="photoMode" 
																value="url" 
																checked={photoUploadMode === 'url'} 
																onChange={(e) => {
																	setPhotoUploadMode(e.target.value);
																	setstudents({ ...students, photo: "" });
																	setErrors({ ...errors, photo: "" });
																}} 
															/>
															<label className="form-check-label" htmlFor="photoUrl">File URL</label>
														</div>
													</div>
													{photoUploadMode === 'upload' ? (
														<input 
															className={`form-control ${errors.photo ? "is-invalid" : ""}`}
															type="file" 
															name="photo" 
															onChange={onChange} 
															required 
														/>
													) : (
														<input 
															className={`form-control ${errors.photo ? "is-invalid" : ""}`}
															type="url" 
															name="photo" 
															value={students.photo} 
															placeholder="Enter photo URL" 
															onChange={onChange} 
															required 
														/>
													)}
													{photoUploadMode === 'upload' && (
														<small className='form-text text-muted'>Valid file types: jpg, jpeg, png, pdf, heic | Max size: 5MB</small>
													)}
													{errors.photo && <div className="invalid-feedback">{errors.photo}</div>}
												</div>
											</div>
											<div className="col-md-6">
												<div className="mb-3">
													<label className="form-label">Valid ID Proof <span className="text-danger">*</span></label>
													<div className="mb-2">
														<div className="form-check form-check-inline">
															<input 
																className="form-check-input" 
																type="radio" 
																id="documentUpload" 
																name="documentMode" 
																value="upload" 
																checked={documentUploadMode === 'upload'} 
																onChange={(e) => {
																	setDocumentUploadMode(e.target.value);
																	setstudents({ ...students, documentid: "" });
																	setErrors({ ...errors, documentid: "" });
																}} 
															/>
															<label className="form-check-label" htmlFor="documentUpload">Upload File</label>
														</div>
														<div className="form-check form-check-inline">
															<input 
																className="form-check-input" 
																type="radio" 
																id="documentUrl" 
																name="documentMode" 
																value="url" 
																checked={documentUploadMode === 'url'} 
																onChange={(e) => {
																	setDocumentUploadMode(e.target.value);
																	setstudents({ ...students, documentid: "" });
																	setErrors({ ...errors, documentid: "" });
																}} 
															/>
															<label className="form-check-label" htmlFor="documentUrl">File URL</label>
														</div>
													</div>
													{documentUploadMode === 'upload' ? (
														<input 
															className={`form-control ${errors.documentid ? "is-invalid" : ""}`}
															type="file" 
															name="documentid" 
															onChange={onChange} 
															required 
														/>
													) : (
														<input 
															className={`form-control ${errors.documentid ? "is-invalid" : ""}`}
															type="url" 
															name="documentid" 
															value={students.documentid} 
															placeholder="Enter document URL" 
															onChange={onChange} 
															required 
														/>
													)}
													{documentUploadMode === 'upload' && (
														<small className='form-text text-muted'>Valid file types: jpg, jpeg, png, pdf, heic | Max size: 5MB</small>
													)}
													{errors.documentid && <div className="invalid-feedback">{errors.documentid}</div>}
												</div>
											</div>
										</div>

										<div className="mb-3">
											<label className="form-label">Role <span className="text-danger">*</span></label>
											<select 
												className={`form-control ${errors.role ? "is-invalid" : ""}`}
												name="role" 
												value={students.role} 
												onChange={onChange} 
												required
											>
												<option value="Student">Student</option>
												{localStorage.getItem('role') === 'Superadmin' && (
													<>
														<option value="Admin">Admin</option>
														<option value="Attendanceofficer">Attendance Officer</option>
													</>
												)}
											</select>
											{errors.role && <div className="invalid-feedback">{errors.role}</div>}
										</div>

										<div className="d-grid">
											<button 
												type="submit" 
												className="btn btn-primary btn-lg" 
												disabled={showSpinner || !isEmailAvailable || !isPhoneAvailable}
											>
												{showSpinner ? (
													<>
														<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
														Creating...
													</>
												) : (
													"Create Student"
												)}
											</button>
										</div>
									</form>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default CreateStudent 